import { useEffect, useState } from "react";
import { UserX } from "lucide-react";
import { logout } from "@/lib/api/auth";
import { getAuthUser } from "@/lib/api/token-store";
import { dismissFaceIntruder, subscribeFaceIntruder } from "@/lib/face/face-auth-policy";
import { FaceCameraPreview } from "@/components/auth/face-camera-preview";
import { Button } from "@/components/ui/button";

/**
 * Shown when face unlock sees an unrecognized person at a signed-in machine and the owner is
 * not in frame. Says whose session this is and locks it when the countdown runs out.
 *
 * Mounted app-wide from App (outside the workspaces), like PinSetupPrompt: it must sit above
 * every frozen tree and survive the session change it is counting down to.
 *
 * Deliberately NOT a Radix Dialog. A dialog is dismissible by Escape and by clicking outside,
 * and traps focus — all wrong for a security notice that someone unauthorized is looking at.
 * This is a plain fixed overlay: the only ways out are the two explicit buttons.
 */
export function FaceIntruderOverlay() {
  const [intruder, setIntruder] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => subscribeFaceIntruder(setIntruder), []);

  useEffect(() => {
    if (!intruder) {
      setSecondsLeft(0);
      return undefined;
    }
    // Derived from the deadline rather than decremented, so a throttled timer (background
    // window, sleeping laptop) can't leave the countdown showing time that has already passed.
    const tick = () => {
      setSecondsLeft(Math.max(0, Math.ceil((intruder.deadlineMs - Date.now()) / 1000)));
    };
    tick();
    const timerId = setInterval(tick, 250);
    return () => clearInterval(timerId);
  }, [intruder]);

  if (!intruder) return null;

  const ownerName = String(getAuthUser()?.name || getAuthUser()?.displayName || "").trim();

  return (
    <div
      // assertive: this is a security notice with a deadline, not a status update — it must
      // interrupt a screen reader rather than wait for a pause.
      role="alertdialog"
      aria-live="assertive"
      aria-labelledby="face-intruder-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
    >
      <div className="w-full max-w-sm space-y-4 rounded-xl border border-destructive/40 bg-card p-6 shadow-2xl">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <UserX className="h-6 w-6 text-destructive" aria-hidden />
          </div>
          <div className="space-y-1.5">
            <h2 id="face-intruder-title" className="text-lg font-semibold text-foreground">
              {ownerName ? `This is ${ownerName}'s session` : "This isn't your session"}
            </h2>
            <p className="text-sm text-muted-foreground">
              We don't recognize you. Please log out so you can sign in with your own account.
            </p>
          </div>

          {/* Shows WHY, not just what: the rightful owner who is simply badly lit or half out of
              frame can see that at a glance and press "It's me" knowing what happened, instead of
              being told they're a stranger with no explanation. Renders nothing if the camera
              isn't running, so the panel degrades to plain text. */}
          <FaceCameraPreview className="w-48" />

          <p className="text-sm text-muted-foreground" aria-live="off">
            Locking in{" "}
            <span className="font-semibold tabular-nums text-destructive">{secondsLeft}s</span>
          </p>
        </div>

        <div className="space-y-2">
          <Button type="button" variant="destructive" className="w-full" onClick={() => void logout()}>
            Log out now
          </Button>
          {/* The escape hatch for the false positive: bad light, a new pair of glasses, someone
              enrolled who simply isn't matching today. Locking is recoverable but disruptive,
              and the rightful owner must always be able to say "no, it's me" — they are already
              inside an authenticated session, so this grants nothing they don't have. */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full font-normal text-muted-foreground"
            onClick={() => dismissFaceIntruder()}
          >
            {ownerName ? `I'm ${ownerName} — stay signed in` : "It's me — stay signed in"}
          </Button>
        </div>
      </div>
    </div>
  );
}
